import { useCallback, useContext } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import type { Principal } from '../../../commonTypes.tsx';
import { toApolloErrorAttributes } from '../../../../controller/analyticsErrorUtils.ts';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../controller/Controller.tsx';

import type { SpecificAccessLiveMutationArgs } from './specificAccessDialogTypes.ts';
import {
	buildUpdateContentDirectRestrictionsBulkRemoveVariables,
	buildUpdateContentDirectRestrictionsRemoveVariables,
	buildUpdateContentDirectRestrictionsVariables,
} from '../../../../controller/mutations/contentDirectRestrictionsPayload.ts';
import type {
	useUpdateContentDirectRestrictionsMutation$data as MutationData,
	useUpdateContentDirectRestrictionsMutation$variables as MutationVariables,
} from '../../../../controller/mutations/__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';
import type { SpecificAccessFlags } from './useSpecificAccessFlags.tsx';

export type PrincipalById = Map<string, { principal: Principal }>;

type MutationPayload = MutationData['confluence_updateContentDirectRestrictions'];

/**
 * Checks if the mutation completed successfully without errors.
 * Centralizes the success/error check to ensure consistent behavior across all mutation paths.
 */
function isMutationSuccess(payload: MutationPayload): boolean {
	return Boolean(payload?.success && (!payload.errors || payload.errors.length === 0));
}

/**
 * Fires an operational analytics event for live mutation errors.
 */
function fireErrorAnalytics({
	createAnalyticsEvent,
	error,
	mutationType,
	principalCount,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	error: unknown;
	mutationType: SpecificAccessLiveMutationArgs['type'];
	principalCount?: number;
}): void {
	createAnalyticsEvent({
		type: 'sendOperationalEvent',
		data: {
			source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			action: 'errored',
			actionSubject: 'specificAccessLiveMutation',
			attributes: {
				...toApolloErrorAttributes(error),
				mutationType,
				principalCount,
			},
		},
	}).fire();
}

/**
 * Executes live mutations for the Specific Access dialog and surfaces the
 * server response so callers can reconcile local state with the authoritative
 * restriction changes.
 */
export function useSpecificAccessLiveMutation({
	cloudId,
	contentId,
	includeInvites,
	principalById,
	mutateDirectRestrictions,
	flags,
	onMutationApplied,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principalById: Map<string, { principal: Principal }>;
	mutateDirectRestrictions: (options: {
		variables: MutationVariables;
	}) => Promise<{ data?: MutationData } | undefined>;
	flags: SpecificAccessFlags;
	onMutationApplied?: (args: {
		payload: NonNullable<MutationData['confluence_updateContentDirectRestrictions']>;
		mutationArgs: SpecificAccessLiveMutationArgs;
	}) => void;
}): {
	triggerLiveMutation: (args: SpecificAccessLiveMutationArgs) => Promise<void>;
} {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const triggerLiveMutation = useCallback(
		async (args: SpecificAccessLiveMutationArgs) => {
			experienceTracker.start({
				name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
			});

			try {
				if (args.type === 'remove-all') {
					const principalsToRemove: Principal[] = [];
					for (const id of args.principalIdsToRemove) {
						const principalEntry = principalById.get(id);
						if (principalEntry) {
							principalsToRemove.push(principalEntry.principal);
						}
					}

					if (principalsToRemove.length === 0) {
						experienceTracker.succeed({
							name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
						});
						return;
					}

					const bulkRemoveVariables = buildUpdateContentDirectRestrictionsBulkRemoveVariables({
						cloudId,
						contentId,
						includeInvites,
						principals: principalsToRemove,
						principalRoles: args.principalRoles,
					});

					const bulkRemoveRes = await mutateDirectRestrictions({ variables: bulkRemoveVariables });
					const bulkRemovePayload = bulkRemoveRes?.data?.confluence_updateContentDirectRestrictions;

					if (!isMutationSuccess(bulkRemovePayload)) {
						const mutationError = new Error('Bulk remove mutation returned unsuccessful payload');
						fireErrorAnalytics({
							createAnalyticsEvent,
							error: mutationError,
							mutationType: args.type,
							principalCount: principalsToRemove.length,
						});
						experienceTracker.fail({
							name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
							error: mutationError,
							attributes: {
								mutationType: args.type,
								principalCount: principalsToRemove.length,
							},
						});
						flags.showAccessChangeFailedFlag();
						return;
					}

					if (bulkRemovePayload && onMutationApplied) {
						onMutationApplied({ payload: bulkRemovePayload, mutationArgs: args });
					}

					experienceTracker.succeed({
						name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
					});
					flags.showAccessUpdatedFlag();
					return;
				}

				const entry = principalById.get(args.principalId);
				if (!entry) {
					experienceTracker.succeed({
						name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
					});
					return;
				}

				const variables =
					args.type === 'remove'
						? buildUpdateContentDirectRestrictionsRemoveVariables({
								cloudId,
								contentId,
								includeInvites,
								principal: entry.principal,
								role: args.previousRole,
							})
						: buildUpdateContentDirectRestrictionsVariables({
								cloudId,
								contentId,
								includeInvites,
								principal: entry.principal,
								previousRole: args.previousRole,
								nextRole: args.nextRole,
							});

				const res = await mutateDirectRestrictions({ variables });
				const payload = res?.data?.confluence_updateContentDirectRestrictions;
				if (!isMutationSuccess(payload)) {
					const mutationError = new Error('Mutation returned unsuccessful payload');
					fireErrorAnalytics({
						createAnalyticsEvent,
						error: mutationError,
						mutationType: args.type,
						principalCount: 1,
					});
					experienceTracker.fail({
						name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
						error: mutationError,
						attributes: {
							mutationType: args.type,
							principalCount: 1,
						},
					});
					flags.showAccessChangeFailedFlag();
					return;
				}

				if (payload && onMutationApplied) {
					onMutationApplied({ payload, mutationArgs: args });
				}

				experienceTracker.succeed({
					name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
				});
				flags.showAccessUpdatedFlag();
			} catch (error) {
				fireErrorAnalytics({
					createAnalyticsEvent,
					error,
					mutationType: args.type,
					principalCount: args.type === 'remove-all' ? args.principalIdsToRemove.length : 1,
				});
				experienceTracker.fail({
					name: SHARE_AND_RESTRICT_DIALOG_RESTRICTION_UPDATE_EXPERIENCE,
					error,
					attributes: {
						mutationType: args.type,
					},
				});
				flags.showAccessChangeFailedFlag();
			}
		},
		[
			cloudId,
			contentId,
			createAnalyticsEvent,
			experienceTracker,
			flags,
			includeInvites,
			mutateDirectRestrictions,
			onMutationApplied,
			principalById,
		],
	);

	return { triggerLiveMutation };
}
